// /platform/ai: Intelligent products. Written for a sceptical mid-market MD: concrete, sourced, honest
// labels, real-looking UI instead of AI imagery. Facts from content.intelligence, content.lifecycle and
// the public AI Statement (/legal/ai/). No vendor names, no customer names, no founder framing.
import { useEffect, useState } from 'react';
import { cta, intelligence, lifecycle } from '../content';
import { Arrow, Button, SmartLink, StatusChip, Swipe, cx, useReveal, usePageMeta } from '../ui';
import { heroUnits, type HeroUnit } from '../heroUnits';
import { BookingWidget } from '../booking/BookingWidget';
import { AskDemo } from './AskDemo';
import { TeamTakeover } from './TeamTakeover';
import { LifecycleStrip } from './LifecycleStrip';
import { GenericVsGrounded } from './GenericVsGrounded';
import { Photo, useAiStyles } from './shared';

const META_TITLE = 'AI product support for manufacturers';
const META_DESC =
  'Give every product you make its own expert. Answers about that exact unit from your own manuals, with the page they came from, and a person one tap away. From unboxing to recycling.';

// One short sentence so the fold holds the headline, the sub and the one action.
const HERO_SUB = 'It knows the exact unit and answers from your own manuals.';

function useNarrow(q = '(max-width: 520px)') {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches;
  const [m, setM] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [q]);
  return m;
}

const FAQS = [
  {
    q: 'Is this just a general chatbot?',
    a: 'No. A general chatbot answers from the open internet and cannot see your product. This assistant answers only from the documents you approve and the record of that exact unit, and hands over to your team when it is unsure.',
  },
  {
    q: 'Can it say something wrong or unsafe?',
    a: 'AI can make mistakes, so it is set up to limit them. It answers only from your documents, uses only the actions you enable, and hands over when unsure or when your rules say so. Your team can take over any conversation. We recommend a person decides anything with safety, financial or legal consequences.',
  },
  {
    q: 'Who sees the conversations?',
    a: 'Your team does, in an inbox where every conversation is linked to the unit. We are adding a notice so owners know from the first message that they are talking to an AI assistant, as the EU AI Act requires. Conversations are kept for two years from last activity unless you delete them sooner, and deletion flows through to the AI indexes.',
  },
  {
    q: 'Does our data train AI models?',
    a: 'Our AI provider is contractually prohibited from training on your prompts, data or outputs, and we train no model shared between customers on your data. What we learn from your product information improves only your own assistant. We may learn from support questions and ratings to improve answers, with personal details and your company and brand names removed first, and you can switch that off.',
  },
];

function useFaqJsonLd() {
  useEffect(() => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'ld-ai-faq';
    el.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, []);
}

/* ---------- 1. Hero ---------- */
function Hero() {
  const [unit, setUnit] = useState<HeroUnit>(heroUnits[0]);
  const narrow = useNarrow();
  return (
    <section className="aip-hero">
      <div className="wrap aip-hero-grid">
        <div className="aip-hero-copy">
          <h1 className="t-display aip-h1">
            AI that <Swipe draw>works.</Swipe>
          </h1>
          <p className="t-lede aip-hero-lede">{HERO_SUB}</p>
          <div className="aip-hero-cta">
            <Button href={cta.primaryHref}>
              {cta.primary} <Arrow />
            </Button>
            <SmartLink href={cta.secondaryHref} className="aip-textlink">
              or take the tour
            </SmartLink>
          </div>
        </div>

        <div className="aip-hero-stage aip-mock">
          <figure className="aip-hero-photo frame">
            <div>
              <Photo src={unit.scene} alt={unit.sceneAlt} eager className="aip-hero-img" width={1600} height={1067} />
            </div>
          </figure>
          <span className="aip-hero-tag">Demo unit, fictional brand</span>
          <AskDemo rotate onUnitChange={setUnit} className="aip-hero-phone" width={narrow ? 262 : 300} />
        </div>
      </div>
    </section>
  );
}

/* ---------- 2. Generic vs grounded, plus what the comparison does not show ---------- */
// Knowing the unit and citing sources are shown in the comparison; rules and take-over in the team
// section. These are the two points left.
const MORE = intelligence.points.filter((p) => /better|assistants/i.test(p.t));

function Contrast() {
  return (
    <section className="section aip-alt" id="generic-vs-grounded">
      <div className="wrap">
        <div className="aip-split-head" data-reveal>
          <h2 className="t-h2">
            Don&rsquo;t guess. <Swipe>Get it right.</Swipe>
          </h2>
          <p className="t-lede aip-split-lede">A general chatbot cannot see the serial, the build or the warranty. It lacks the context.</p>
        </div>
        <div className="aip-mock mt-12" data-reveal>
          <GenericVsGrounded />
        </div>
        <ul className="aip-more">
          {MORE.map((p, i) => (
            <li key={p.t} data-reveal style={{ ['--reveal-delay' as string]: `${i * 0.06}s` }}>
              <div className="flex items-center justify-between gap-4">
                <h3 className="aip-more-t">{p.t}</h3>
                <StatusChip status={p.status} />
              </div>
              <p className="aip-more-d">{p.d}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------- 4. Team side ---------- */
function Team() {
  const controls = [
    { t: 'You set the rules', d: 'Tone, safety handling and when it must hand over to a person.' },
    { t: 'You choose the sources', d: 'Each document is in or out. Nothing else gets read.' },
    { t: 'You can switch it off', d: 'Per feature, or for the whole account.' },
  ];
  return (
    <section className="section on-dark aip-dark" id="your-team">
      <div className="wrap">
        <div className="aip-split-head" data-reveal>
          <h2 className="t-h2">
            Humans <Swipe>in control.</Swipe>
          </h2>
          <p className="t-lede aip-split-lede">Your team sees every conversation and takes over in one click. It never approves or refuses a claim.</p>
        </div>
        <div className="aip-mock mt-12" data-reveal>
          <TeamTakeover tone="dark" />
        </div>
        <div className="aip-team-foot">
          <ul className="aip-controls">
            {controls.map((c, i) => (
              <li key={c.t} data-reveal style={{ ['--reveal-delay' as string]: `${i * 0.06}s` }}>
                <span className="aip-controls-t">{c.t}</span>
                <span className="aip-controls-d">{c.d}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------- 5. Lifecycle ---------- */
function Lifecycle() {
  return (
    <section className="section" id="lifecycle">
      <div className="wrap">
        <div className="aip-split-head" data-reveal>
          <h2 className="t-h2">
            {lifecycle.title[0]} <Swipe>{lifecycle.title[1]}</Swipe>
          </h2>
          <p className="t-lede aip-split-lede">{lifecycle.lede}</p>
        </div>
        <div className="aip-mock mt-12" data-reveal>
          <LifecycleStrip photo={{ src: '/images/v2/gen/heatpump-home.jpg', alt: 'A homeowner adjusting a wall controller in a calm hallway at home' }} />
        </div>
      </div>
    </section>
  );
}

/* ---------- 8. FAQ ---------- */
function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  useFaqJsonLd();
  return (
    <section className="section aip-alt" id="faq">
      <div className="wrap aip-faq">
        <div data-reveal>
          <h2 className="t-h2">The questions a careful buyer asks.</h2>
          <p className="aip-faq-src">
            Everything here is in our public{' '}
            <SmartLink href="/legal/ai/" className="aip-link">
              AI Statement
            </SmartLink>
            , which forms part of the contract.
          </p>
        </div>
        <ul className="aip-faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <li key={f.q} className="aip-faq-item">
                <h3>
                  <button type="button" className="aip-faq-q" aria-expanded={isOpen} aria-controls={`aip-faq-${i}`} onClick={() => setOpen(isOpen ? null : i)}>
                    <span>{f.q}</span>
                    <span className={cx('aip-faq-ico', isOpen && 'is-open')} aria-hidden="true" />
                  </button>
                </h3>
                <div id={`aip-faq-${i}`} className="aip-faq-a" data-open={isOpen} role="region">
                  <div>
                    <p>{f.a}</p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/* ---------- 9. CTA ---------- */
function Cta() {
  return (
    <section className="section on-dark aip-dark aip-cta" id="book">
      <div className="wrap aip-cta-grid">
        <div data-reveal>
          <h2 className="t-h2">
            Meet your product&rsquo;s <Swipe>expert.</Swipe>
          </h2>
          <p className="t-lede mt-6">
            Send one product&rsquo;s manual. On a 30-minute call, we show you how it would answer your owners.
          </p>
        </div>
        <div data-reveal>
          <BookingWidget compact tone="dark" source="platform-ai" />
        </div>
      </div>
    </section>
  );
}

export default function IntelligencePage() {
  usePageMeta(META_TITLE, META_DESC);
  useAiStyles();
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className="aip">
      <style>{PAGE_CSS}</style>
      <Hero />
      <Contrast />
      <Team />
      <Lifecycle />
      <Faq />
      <Cta />
    </div>
  );
}

const PAGE_CSS = `
.aip-alt { background: var(--bm-paper-alt); }
.aip-dark { background: var(--bm-indigo); }
.aip-textlink { font-weight: 600; color: var(--bm-indigo); font-size: 15.5px; }
.aip-textlink:hover { text-decoration: underline; text-underline-offset: 4px; }
.aip-link { color: var(--bm-emerald); font-weight: 600; text-decoration: underline; text-underline-offset: 3px; }

/* hero */
.aip-hero { padding: clamp(36px, 5vw, 72px) 0 clamp(64px, 8vw, 112px); overflow: hidden; }
.aip-hero-grid { display: grid; grid-template-columns: minmax(0, 1fr) 520px; gap: 40px; align-items: center; }
.v2 .aip-h1 { font-size: clamp(44px, 4.9vw, 70px); }
.aip-hero-lede { margin-top: 24px; max-width: 540px; }
.aip-hero-cta { margin-top: 32px; display: flex; align-items: center; gap: 22px; flex-wrap: wrap; }
.aip-hero-status { margin-top: 24px; display: flex; align-items: center; gap: 10px; font-size: 14px; color: var(--bm-muted); }
.aip-hero-stage { position: relative; height: 660px; }
.aip-hero-photo { position: absolute; right: 0; top: 0; width: 380px; height: 580px; margin: 0; }
.aip-hero-photo > div { height: 100%; }
.aip-hero-img { width: 100%; height: 100%; object-fit: cover; }
.aip-hero-tag { position: absolute; right: 18px; top: 18px; font-size: 11.5px; font-weight: 500; color: #fff; background: rgba(24,19,67,.62); padding: 4px 10px; border-radius: 99px; }
.aip-hero-phone { position: absolute; left: 0; top: 36px; }
@media (max-width: 1180px) {
  .aip-hero-grid { grid-template-columns: minmax(0,1fr) 470px; gap: 32px; }
  .aip-hero-photo { width: 330px; }
}
@media (max-width: 980px) {
  .aip-hero-grid { grid-template-columns: minmax(0, 1fr); }
  .aip-hero-stage { max-width: 540px; width: 100%; margin: 12px auto 0; }
  .aip-hero-photo { width: 64%; }
}
@media (max-width: 520px) {
  .aip-hero-stage { height: 600px; margin-top: 4px; }
  .aip-hero-photo { width: 66%; height: 380px; right: -8px; }
  .aip-hero-tag { right: 10px; top: 12px; }
  .aip-hero-phone { left: -2px; top: 54px; }
}

/* split heads: heading left, lede right */
.aip-split-head { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr); gap: 48px; align-items: end; }
.aip-split-lede { max-width: 480px; padding-bottom: 6px; }
@media (max-width: 900px) { .aip-split-head { grid-template-columns: minmax(0, 1fr); gap: 18px; } }

/* the two points the comparison does not show */
.aip-more { margin-top: 40px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 48px; border-top: 1px solid var(--bm-line); }
.aip-more li { padding: 22px 0 0; }
.aip-more-t { font-weight: 700; font-size: 19px; letter-spacing: -0.015em; color: var(--bm-indigo); }
.aip-more-d { margin-top: 6px; color: var(--bm-muted); font-size: 16px; max-width: 460px; }
@media (max-width: 760px) { .aip-more { grid-template-columns: minmax(0, 1fr); } .aip-more li + li { margin-top: 18px; padding-top: 18px; border-top: 1px solid var(--bm-line); } }

/* team */
.aip-team-foot { margin-top: 48px; padding-top: 28px; border-top: 1px solid rgba(255,255,255,.14); }
.aip-controls { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 40px; }
.aip-controls li { display: grid; gap: 4px; align-content: start; }
.aip-controls-t { font-weight: 600; color: #fff; }
.aip-controls-d { color: rgba(255,255,255,.66); font-size: 15.5px; }
@media (max-width: 900px) {
  .aip-team-foot { margin-top: 36px; padding-top: 8px; }
  .aip-controls { grid-template-columns: minmax(0, 1fr); gap: 0; }
  .aip-controls li { gap: 2px; padding: 14px 0; border-bottom: 1px solid rgba(255,255,255,.12); }
}

/* faq */
.aip-faq { display: grid; grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr); gap: 72px; }
.aip-faq-src { margin-top: 20px; color: var(--bm-muted); max-width: 360px; }
.aip-faq-list { border-top: 1px solid var(--bm-line); }
.aip-faq-item { border-bottom: 1px solid var(--bm-line); }
.aip-faq-q { width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 20px; padding: 22px 0; text-align: left; font-weight: 700; font-size: clamp(18px, 1.6vw, 21px); letter-spacing: -0.015em; color: var(--bm-indigo); background: none; border: 0; cursor: pointer; }
.aip-faq-ico { position: relative; width: 16px; height: 16px; flex: none; }
.aip-faq-ico::before, .aip-faq-ico::after { content: ''; position: absolute; left: 0; top: 7px; width: 16px; height: 2px; border-radius: 2px; background: var(--bm-indigo); transition: transform .2s var(--bm-ease); }
.aip-faq-ico::after { transform: rotate(90deg); }
.aip-faq-ico.is-open::after { transform: rotate(0deg); }
.aip-faq-a { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .25s var(--bm-ease); }
.aip-faq-a[data-open="true"] { grid-template-rows: 1fr; }
.aip-faq-a > div { overflow: hidden; }
.aip-faq-a[data-open="false"] > div { visibility: hidden; transition: visibility 0s .25s; }
.aip-faq-a p { padding: 0 40px 24px 0; color: var(--bm-muted); font-size: 16.5px; max-width: 640px; }
@media (max-width: 900px) { .aip-faq { grid-template-columns: minmax(0, 1fr); gap: 28px; } .aip-faq-a p { padding-right: 0; } }

/* cta */
.aip-cta-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 480px); gap: 64px; align-items: center; }
@media (max-width: 900px) { .aip-cta-grid { grid-template-columns: minmax(0, 1fr); gap: 36px; } }
`;
